import { Refreshable } from '../../utils/refreshable/IRefreshable';

/**
 * Priority levels for service execution.
 * Higher number means higher priority and earlier execution.
 */
export enum ServicePriority {
  MESSAGE = 1,
  LOW = 2,
  MEDIUM = 5,
  HIGH = 10,
  CRITICAL = 15
}

/**
 * Type definition for service method functions with improved typing
 */
export type ServiceMethodType = () => Promise<unknown> | unknown;

/**
 * Container interface for managing service initialization and refresh operations.
 * Supports prioritized execution of services.
 */
export interface IContainer {
  /**
   * Adds an initialization method to the container with an optional priority level.
   * @param serviceInitMethod - The service initialization method to add
   * @param priority - The priority level for execution (default: MEDIUM)
   * @param serviceName - Optional name of the service/class for better error reporting
   */
  addInitServices(
    serviceInitMethod,
    priority?: ServicePriority,
    serviceName?: string
  ): void;

  /**
   * Adds a refresh method to the container with an optional priority level.
   * @param serviceRefreshMethod - The service refresh method to add
   * @param priority - The priority level for execution (default: MEDIUM)
   * @param serviceName - Optional name of the service/class for better error reporting
   */
  addRefreshServices(
    serviceRefreshMethod,
    priority?: ServicePriority,
    serviceName?: string
  ): void;

  /**
   * Adds a Refreshable service to the container with an optional priority level.
   * @param service - The Refreshable service to add
   * @param priority - The priority level for execution (default: MEDIUM)
   */
  addService(service: Refreshable, priority?: ServicePriority): void;

  /**
   * Initializes all registered services based on their priority.
   * @returns A Promise that resolves when all services are initialized
   */
  initializeContextServices(): Promise<void>;

  /**
   * Refreshes all registered services based on their priority.
   * @returns A Promise that resolves when all services are refreshed
   */
  refreshContext(): Promise<void>;
}
